import { signal } from '@preact/signals'
import { hasOwnProperties } from 'helpers/utils'
import { select, getHeight } from 'helpers/dom'
import { QUERY_MAPPING, FILTER_SELECTORS } from 'libs/search-native/search-native-utils'
import { fetchSearchResults } from 'libs/search-native/search-native-service'

const selectors = {
  HEADER_SELECTOR: 'site-header',
  COLLECTION_INFO_SELECTOR: '.js-collection-info',
  TOPBAR_SELECTOR: '.js-topbar',
  SCROLL_POSITION_SESSION: 'scrollPosition',
  SEARCH: 'search'
}

const activeFilters = signal(false)
const isLoading = signal(true)
const isFiltering = signal(false)
const searchQuery = signal('')
const originalQuery = signal('')
const handle = signal('')
const sortOrder = signal(null)
const currentPage = signal(1)
const selectedValues = signal({})
const sortOptions = signal([])
const filters = signal([])
const pagination = signal(null)
const products = signal(null)
const localSort = signal(null)

const TYPES = {
  event: {
    init: 'init',
    popState: 'popState'
  },
  apply: {
    initial: 'initial',
    filtering: 'filtering'
  }
}

const setProducts = (items) => {
  products.value = items
}
const setLocalSort = (sortValue) => {
  localSort.value = sortValue
}
const setSelectedValues = (items) => {
  selectedValues.value = items
}
const setActiveFilters = () => {
  activeFilters.value = !activeFilters.value
}

const updateHandle = (item) => {
  handle.value = item
}
const updateSearchQuery = (query) => {
  searchQuery.value = query
}
const updateOriginalQuery = (query) => {
  originalQuery.value = query
}

const scrollToPopState = (collectionHandle = selectors.SEARCH) => {
  const scrollPositionKey = `${selectors.SCROLL_POSITION_SESSION}_${collectionHandle}`
  const position = sessionStorage.getItem(scrollPositionKey)

  if (position && position < document.documentElement.scrollHeight) {
    window.scrollTo({
      top: parseInt(position, 10),
      behavior: 'smooth'
    })
    sessionStorage.removeItem(scrollPositionKey)
  }
}

const saveScrollPosition = (collectionHandle = selectors.SEARCH) => {
  sessionStorage.setItem(`${selectors.SCROLL_POSITION_SESSION}_${collectionHandle}`, window.scrollY.toString())
}

const scrollToTopPage = () => {
  const collectionInfoEl = select(selectors.COLLECTION_INFO_SELECTOR)
  const topBarEl = select(selectors.TOPBAR_SELECTOR)
  const headerEl = select(selectors.HEADER_SELECTOR)

  if (!collectionInfoEl) {
    return
  }

  const collectionInfoOffset = collectionInfoEl.getBoundingClientRect().top + window.scrollY
  // Double heights when on top page
  const headerHeight = window.scrollY < 1 ? getHeight(headerEl) * 2 : getHeight(headerEl)
  const topBarHeight = topBarEl && window.scrollY < 1 ? getHeight(topBarEl) * 2 : 0
  const scrollTarget = collectionInfoOffset - headerHeight - topBarHeight

  setTimeout(() => {
    window.scrollTo({
      top: scrollTarget < 0 ? 0 : scrollTarget,
      behavior: 'smooth'
    })
  }, 100)
}

const changeQueryUrl = (payload = {}) => {
  const queryParams = new URLSearchParams(window.location.search)
  const queryParams2 = new URLSearchParams(window.location.search)

  queryParams2.forEach((_, key) => {
    const hasKey = Object.entries(QUERY_MAPPING).some((item) => key.indexOf(item[1].code) === 0)

    if (hasKey) {
      queryParams.delete(key)
    }
  })

  if (payload.searchQuery || searchQuery.value) {
    queryParams.append(QUERY_MAPPING.search.code, payload.searchQuery || searchQuery.value)
  }

  const originalQueryValue = originalQuery.value || payload.originalQuery || ''
  const searchQueryValue = searchQuery.value || payload.searchQuery || ''

  if (originalQueryValue && originalQueryValue !== searchQueryValue) {
    queryParams.append(QUERY_MAPPING.search.codeOrigin, payload.originalQuery || originalQuery.value)
  }

  if (payload.selectedValues && Object.keys(payload.selectedValues).length > 0) {
    Object.entries(payload.selectedValues).forEach(([key, values]) => {
      if (values.length > 0) {
        if (key === `${QUERY_MAPPING.filter.param}${QUERY_MAPPING.price.param}`) {
          queryParams.append(
            `${QUERY_MAPPING.filter.param}${QUERY_MAPPING.price.param}.${QUERY_MAPPING.price.low}`,
            values[0]
          )
          queryParams.append(
            `${QUERY_MAPPING.filter.param}${QUERY_MAPPING.price.param}.${QUERY_MAPPING.price.high}`,
            values[1]
          )
        } else {
          values.forEach((value) => {
            queryParams.append(key, value.value || value.label)
          })
        }
      }
    })
  }

  if (payload.sort_by && payload.sort_by.direction) {
    queryParams.append(`${QUERY_MAPPING.sort.param}`, payload.sort_by.direction)
  }

  if (payload.page > 1) {
    queryParams.append(QUERY_MAPPING.page.param, payload.page)
  }

  const finalUrl = `${window.location.origin}${window.location.pathname}${queryParams.toString() !== '' ? '?' : ''}${queryParams.toString()}`

  if (payload.event_type === 'init') {
    window.history.replaceState(
      {
        params: payload,
        url: finalUrl
      },
      null,
      finalUrl
    )
  } else if (payload.event_type !== 'popState') {
    window.history.pushState(
      {
        params: payload,
        url: finalUrl
      },
      null,
      finalUrl
    )
  }
}

const spliceInlineContent = (productList, page) => {
  const { inlineBanner } = window.collectionState

  if (inlineBanner?.length) {
    const uniqueBannerByPosition = Array.from(new Map(inlineBanner.map((item) => [item.position, item])).values())

    if (page === 1) {
      uniqueBannerByPosition
        .sort((a, b) => a.position - b.position)
        .forEach((banner, index) => {
          const spliceIndex = Number(banner.position) - (index + 1)

          productList.splice(spliceIndex, 0, banner)
        })
    }
  }

  return productList
}

const applyFilters = async (payload, type = TYPES.apply.filtering, scrollToTop = false) => {
  const isInitial = type === TYPES.apply.initial
  const currentPayload = { ...payload }

  if (isInitial) {
    isLoading.value = true
  } else {
    isFiltering.value = true
  }

  const isSearch =
    hasOwnProperties(QUERY_MAPPING.search.param, currentPayload) ||
    window.location.pathname.includes(QUERY_MAPPING.search.path)

  const assignValue = (key, signalStore) => {
    if (currentPayload[key]) {
      const updatedSignalStore = signalStore

      updatedSignalStore.value = currentPayload[key]
    } else {
      currentPayload[key] = signalStore.value || ''
    }
  }

  if (isSearch) {
    assignValue(QUERY_MAPPING.search.param, searchQuery)
    assignValue(QUERY_MAPPING.search.originalQuery, originalQuery)
  } else {
    assignValue(QUERY_MAPPING.handle.code, handle)
  }

  assignValue(QUERY_MAPPING.sort.param, sortOrder)
  currentPage.value = currentPayload.page > 1 ? currentPayload.page : 1
  currentPayload.page = currentPage.value

  if (currentPayload.selectedValues) {
    setSelectedValues(
      Object.fromEntries(Object.entries(currentPayload.selectedValues).filter(([_, value]) => value.length > 0))
    )
  } else {
    currentPayload.selectedValues = selectedValues.value
  }

  try {
    const response = await fetchSearchResults(currentPayload, isSearch)

    if (!response) return

    if (isSearch) {
      document.title = document.title.replace(/\d+/, response.pagination.totalProducts)
    }

    if (currentPayload.page === 1 || currentPayload.forceUpdate) {
      setProducts(spliceInlineContent(response.products, currentPayload.page))
    } else {
      products.value =
        currentPayload.paginationType === 'prev'
          ? [...response.products, ...products.value]
          : [...products.value, ...response.products]
    }

    const sortOptionsOrder = ['manual', 'created-descending', 'best-selling', 'price-descending', 'price-ascending']

    const sortOptionsUpdate = response.sorting.options.sort(
      (a, b) => sortOptionsOrder.indexOf(a.direction) - sortOptionsOrder.indexOf(b.direction)
    )

    sortOptions.value = sortOptionsUpdate
    pagination.value = response.pagination

    if (currentPayload.event_type === TYPES.event.init) {
      filters.value = response.filters
    }

    changeQueryUrl(currentPayload)

    scrollToTop && scrollToTopPage()
  } catch (error) {
    console.warn('Error in applyFilters', error)
  } finally {
    if (isInitial) {
      isLoading.value = false
    } else {
      isFiltering.value = false
    }
  }
}

const getDataProducts = async ({ payload, isScroll = false }) => {
  const { page = 1, forceUpdate } = payload

  if (forceUpdate) {
    await applyFilters(payload, TYPES.apply.initial)

    return
  }

  const currentPayload = { ...payload, history: false }

  if (!isScroll) {
    const plpElement = select(FILTER_SELECTORS.element.collection)
    const headerElement = select(selectors.HEADER_SELECTOR)
    const offset = plpElement.offsetTop - getHeight(headerElement)

    window.scrollTo(0, offset)
  }

  for (let pageItem = 1; pageItem <= page; pageItem++) {
    const updatedPayload = { ...currentPayload, page: pageItem, history: pageItem === page }

    // eslint-disable-next-line no-await-in-loop
    await applyFilters(updatedPayload, TYPES.apply.initial)
  }

  if (page === currentPage.value && isScroll) {
    scrollToPopState(payload.handle)
  }
}

const initFilters = async (payload = {}, scrollToTop = false) => {
  window.history.scrollRestoration = 'manual'
  scrollToTop && scrollToTopPage()

  const updatedPayload = { ...payload, event_type: TYPES.event.init }

  await getDataProducts({ payload: updatedPayload, isScroll: true })
}
const onPopState = async (payload = {}) => {
  setProducts([])

  const updatedPayload = { ...payload, event_type: TYPES.event.popState }

  getDataProducts({ payload: updatedPayload, isScroll: false })
}

const goToPage = async ([page, paginationType = 'more'], searchType) => {
  const payload = {
    sort: sortOrder.value,
    selectedValues: selectedValues.value,
    page,
    paginationType,
    ...(searchType && { searchType })
  }

  return applyFilters(payload, TYPES.apply.initial)
}

const applySort = async (sort) => {
  const payload = {
    sort,
    page: 1,
    selectedValues: selectedValues.value
  }

  sortOrder.value = sort

  return applyFilters(payload, TYPES.apply.initial, true)
}

const resetFilters = (close = false) => {
  const payload = {
    selectedValues: {},
    forceUpdate: true,
    event_type: TYPES.event.init
  }

  initFilters(payload, true)

  if (close) {
    setActiveFilters()
  }
}

export {
  isLoading,
  isFiltering,
  products,
  pagination,
  sortOptions,
  sortOrder,
  filters,
  activeFilters,
  selectedValues,
  localSort,
  setActiveFilters,
  initFilters,
  onPopState,
  updateSearchQuery,
  updateOriginalQuery,
  updateHandle,
  applyFilters,
  applySort,
  goToPage,
  resetFilters,
  setLocalSort,
  saveScrollPosition
}
